class VariantRadiosWrapper extends HTMLElement {
  constructor() {
    super();

    // listen to the variant radios change on the Product summary and on the Size drawer
    this.querySelectorAll('[data-variant-radios]').forEach((variantRadio) =>
      variantRadio.addEventListener('change', this.onVariantChange.bind(this))
    );
    this.querySelectorAll('[data-option-quantity]').forEach((variantQuantity) =>
      variantQuantity.addEventListener('change', this.onBulkQuantityChange.bind(this))
    );
  }

  onVariantChange() {
    this.updateOptions();
    this.updateMasterId();

    if (!this.currentVariant) {
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.dispatchVariantChange();
    }
  }

  onBulkQuantityChange(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    let disableButton = true;
    this.querySelectorAll('[data-option-quantity]').forEach((variantQuantity) => {
      if (variantQuantity.value > 0) {
        disableButton = false;
      }
    });

    this.toggleAddButton(disableButton, '', false);
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    this.options = fieldsets.map((fieldset) => {
      const radiosChecked = Array.from(fieldset.querySelectorAll('input')).find(
        (radio) => radio.checked
      );
      // before returning, check if there's a checked value - Size is not pre-selected on load
      if (radiosChecked) {
        return Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked).value;
      } else {
        return undefined;
      }
    });

    // move the Size value to the right index on the options Array so it can match the Options order on Shopify Admin
    if (this.querySelector('[data-size-drawer]')) {
      const fromIndex = this.options.length - 1; // it will always be on the last position because it's loaded after the regular options
      const toIndex =
        this.querySelector('[data-size-drawer]').querySelector('fieldset').dataset.optionPosition;
      this.options.splice(toIndex, 0, this.options.splice(fromIndex, 1)[0]);
    }
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  dispatchVariantChange() {
    document.dispatchEvent(
      new CustomEvent('variant-change', {
        bubbles: true,
        variant: this.currentVariant,
        detail: {
          variant: this.currentVariant,
        },
      })
    );
  }

  updateMedia() {
    if (!this.currentVariant || !this.currentVariant?.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );
    if (!newMedia) return;
    const parent = newMedia.parentElement;
    parent.prepend(newMedia);
    window.setTimeout(() => {
      parent.scroll(0, 0);
    });
  }

  updateURL() {
    if (!this.currentVariant) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        document.getElementById(`price-${this.dataset.section}`)?.classList.remove('hidden');

        // Update the inventory quantity hidden input
        const inventoryQtyDestination = document
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        const inventoryQtySource = html
          .querySelector(`#product-form-${this.dataset.section}`)
          .querySelector('input[name="inventory_quantity"]');
        inventoryQtyDestination.value = inventoryQtySource.value;

        let available = false;
        if (this.currentVariant) {
          available = this.currentVariant.available;
          if (available && window.productValues.tags.includes('alwaysbackorder')) {
            available = true;
          } else if (
            available &&
            inventoryQtyDestination.value <= 0 &&
            !window.userValues?.tags.includes('backorder')
          ) {
            available = false;
          }

          this.toggleAddButton(!available, window.variantStrings.soldOut);
        }

        // Update the stock information visibility - Only show when the variant is unavailable
        if (this.querySelector('[data-stock-information]')) {
          if (inventoryQtySource.value <= '0' && !available) {
            // update the Stock information element
            const stockElementDestination = this.querySelector('[data-stock-information]');
            const stockElementSource = html.querySelector('[data-stock-information]');
            stockElementDestination.innerHTML = stockElementSource.innerHTML;

            // hide the days to new stock when the value is <= 0
            const minutesToNewStock = +stockElementSource.querySelector(
              '[data-stock-variant-information]'
            ).dataset.stockVariantInformation;
            if (minutesToNewStock > 0) {
              this.querySelector('[data-stock-variant-information]').classList.remove('hidden');
            } else {
              this.querySelector('[data-stock-variant-information]').classList.add('hidden');
              this.querySelector('[data-stock-variant-information]').innerHTML = '';
            }

            // show stock info
            this.querySelector('[data-stock-information]').classList.remove('hidden');
          } else {
            // hide stock info
            this.querySelector('[data-stock-information]').classList.add('hidden');
          }
        }

        // Update the Product collapsibles with Variant information on variant change
        let collapsibleWithVariantInfoDestination = this.closest(
          '[data-product-info]'
        ).querySelector('[data-product-collapsible-with-variant-info]');
        if (collapsibleWithVariantInfoDestination) {
          const collapsibleWithVariantInfoSource = html.querySelector(
            '[data-product-collapsible-with-variant-info]'
          );
          collapsibleWithVariantInfoDestination.innerHTML =
            collapsibleWithVariantInfoSource.innerHTML;
        }
      });
  }

  /**
   * toggleAddButton
   * @param {Boolean} disable
   * @param {String} text
   * @param {Boolean} modifyClass
   * @returns {void}
   */
  toggleAddButton(disable = true, text, modifyClass = true) {
    // get the button on the PDP Summary or the button on the Size drawer if there's one
    let addButton, initButton;

    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (this.querySelector('[data-size-drawer]')) {
      addButton = productForm?.querySelector('[data-size-drawer]').querySelector('[name="add"]');
      initButton = productForm
        ?.querySelector('[data-size-drawer]')
        .querySelector('[data-embroidery-init]');
    } else {
      addButton = productForm?.querySelector('[name="add"]');
      initButton = false;
    }

    const addButtonText = addButton.querySelector('[data-add-to-cart-button-text]');
    if (!addButton || !addButtonText) return;

    if (disable) {
      addButton.setAttribute('disabled', true);
      initButton?.setAttribute('disabled', true);

      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      initButton?.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    // don't set the button to unavailable when the variants haven't been chosen
    if (this.options.includes(undefined)) {
      return;
    }

    let addButton;
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    // get the button on the PDP Summary or the button on the Size drawer if there's one
    if (this.querySelector('[data-size-drawer]')) {
      addButton = productForm?.querySelector('[data-size-drawer]').querySelector('[name="add"]');
    } else {
      addButton = productForm?.querySelector('[name="add"]');
    }
    if (!addButton) return;
    addButton.textContent = window.variantStrings.unavailable;
    document.getElementById(`price-${this.dataset.section}`)?.classList.add('hidden');
  }

  getVariantData() {
    this.variantData =
      this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

if (!customElements.get('variants-and-buy-buttons')) {
  customElements.define('variants-and-buy-buttons', VariantRadiosWrapper);
}
